import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { Cow } from '../models/types';
import { getCowStatusColor } from '../utils/helpers';

interface CowTransactionItemProps {
    cow: Cow;
    onPress: () => void;
}

export const CowTransactionItem: React.FC<CowTransactionItemProps> = ({ cow, onPress }) => {
    const statusColor = getCowStatusColor(cow.currentStatus);

    const getFormattedDate = (date: string | undefined) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getStatusLabel = () => {
        if (cow.isPregnant && cow.currentStatus === 'active') {
            return 'PREGNANT';
        }
        return cow.currentStatus.toUpperCase();
    };

    const getStatusBgColor = () => {
        if (cow.isPregnant && cow.currentStatus === 'active') {
            return theme.colors.warning;
        }
        return statusColor;
    };

    // Icon background - lighter shade based on status
    const getIconBg = () => {
        if (cow.currentStatus === 'sold') {
            return '#FFF3E0'; // light orange
        } else if (cow.currentStatus === 'active') {
            return '#E8F5E9'; // light green
        } else if (cow.isPregnant) {
            return '#FFF8E1'; // light yellow
        }
        return '#FFEBEE'; // light red for deceased
    };

    return (
        <TouchableOpacity
            style={styles.container}
            onPress={onPress}
            activeOpacity={0.7}
        >
            {/* Left: Icon */}
            <View style={[styles.iconContainer, { backgroundColor: getIconBg() }]}>
                <Icon name="cow" size={24} color={getStatusBgColor()} />
            </View>

            {/* Center: Content */}
            <View style={styles.mainContent}>
                {/* Status Label */}
                {/* <Text style={[styles.statusLabel, { color: getStatusBgColor() }]}>
                    {getStatusLabel()}
                </Text> */}

                {/* Main Title: COW ID • Name */}
                <Text style={styles.cowTitle} numberOfLines={1}>
                    {cow.name ? `${cow.id} • ${cow.name}` : cow.id}
                </Text>

                {/* Subtitle: Breed • Date */}
                <Text style={styles.subtitleText} numberOfLines={1}>
                    {getFormattedDate(cow.purchaseDate)}
                </Text>

                <Text style={styles.subtitleText} numberOfLines={1}>
                    {cow.breed ? `${cow.breed} ` : ''}
                </Text>
            </View>

            {/* Right: Status Badge & Arrow */}
            <View style={styles.rightSection}>
                <View style={[
                    styles.statusBadge,
                    {
                        backgroundColor: getStatusBgColor() + '15',
                        borderColor: getStatusBgColor(),
                    }
                ]}>
                    <Text style={[styles.statusBadgeText, { color: getStatusBgColor() }]}>
                        {getStatusLabel()}
                    </Text>
                </View>
                <Icon
                    name="chevron-right"
                    size={22}
                    color={theme.colors.textLight}
                    style={styles.arrowIcon}
                />
            </View>
        </TouchableOpacity>
    );
};

export const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.md,
        marginHorizontal: theme.spacing.md,
        marginVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.lg,
        ...theme.shadows.sm,
    },
    iconContainer: {
        width: 44,
        height: 44,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    mainContent: {
        flex: 1,
        paddingRight: theme.spacing.sm,
    },
    statusLabel: {
        fontWeight: '600',
        fontSize: 10,
        letterSpacing: 0.3,
        marginBottom: 2,
    },
    cowTitle: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: 2,
    },
    subtitleText: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    rightSection: {
        // flexDirection: 'row',
        alignItems: 'flex-end',
        gap: theme.spacing.xs,
    },
    statusBadge: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: 4,
        borderRadius: theme.borderRadius.sm,
        borderWidth: 1,
    },
    statusBadgeText: {
        fontWeight: '700',
        fontSize: 10,
        letterSpacing: 0.3,
    },
    arrowIcon: {
        marginLeft: theme.spacing.sm,
    },
    separator: {
        height: theme.spacing.xs,
    },
});
